import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import type { ComponentFilters } from '@/features/payroll-components/api'
import { ComponentCatalogue } from '@/features/payroll-components/component-catalogue'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the component catalogue.
 *
 * Search lives in URL search params so a filtered view is linkable and
 * survives a reload. The shell validates the shape; the feature does the
 * filtering (client-side until the API carries a search contract).
 *
 * The grant guard is presentation only — the API enforces
 * `payroll_component.view` on every component endpoint. The fallback is the
 * runs list, the payroll landing screen; a user with neither bounces to the
 * dashboard from there.
 */
export const Route = createFileRoute('/_authenticated/payroll/components')({
  validateSearch: (search: Record<string, unknown>): ComponentFilters => ({
    search: typeof search.search === 'string' && search.search ? search.search : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'payroll_component.view')) {
      throw redirect({ to: '/payroll/runs' })
    }
  },
  component: PayrollComponentsRoute,
})

function PayrollComponentsRoute() {
  const navigate = useNavigate()
  const params = Route.useSearch()

  return (
    <ComponentCatalogue
      params={params}
      onParamsChange={(next) => void navigate({ to: '/payroll/components', search: next as never })}
    />
  )
}
